import React, {useEffect, useState} from 'react';
import {Box} from '@mui/material';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../common/arr-button/ArrButton';
import {t} from 'i18next';
import {QuickBatch, IOptions, ISortOptions} from './quick-batch/QuickBatch';
import {ISelectItem} from '../../../shared/types';
import {ArrTooltipTitle} from '../../common/arr-tooltip-title/ArrTooltipTitle';
import './QuickBatchFlyout.scss';
import {isEmpty} from 'lodash';
import {
  useGetBulkTagSelectedDocCount,
  useGetQuickAssign,
  useGetUnAssignedUsers,
  useQuickCreate,
  useAddAssignedReviewers,
} from '../../../hooks/assignments/useAssignments';
import {MAX_FOLDER_NAME_LENGTH} from '../../../shared/constants';
import axios from 'axios';

// Types Definitions
// --------------------------------
interface QuickBatchFlyoutProps {
  isOpen: boolean;
  selectedIds?: string[];
  onContinue: (assignmentId?: number, docCount?: number, val?: string) => void;
  onClose: () => void;
  masterDocListId: number;
}

interface IFormvalues {
  assignName: string;
  codingForm: string | null;
  reviewers: IOptions[];
  sortOrder: string;
}

const sortOptions: ISortOptions[] = [
  {
    label: 'Optimized Sort',
    value: 'optimizedSort',
  },
  {
    label: 'Chronological',
    value: 'chronological',
  },
];
// ------------------

// ----------------------------------
// QuickBatchFlyout Component
// ----------------------------------
const enQuickBatch = 'actionMenu.quickBatchFlyout';
const QuickBatchFlyout: React.FC<QuickBatchFlyoutProps> = ({
  isOpen = false,
  onClose,
  onContinue,
  selectedIds = [],
  masterDocListId,
}) => {
  const [formValues, setFormValues] = useState<IFormvalues>({
    assignName: '',
    codingForm: '',
    reviewers: [],
    sortOrder: sortOptions[0].value,
  });
  const [disableContinue, setDisableContinue] = useState<boolean>(true);
  const [reveiwersOptions, setReveiwersOptions] = useState<IOptions[]>([]);
  const [codingFormOptions, setCodingFormOptions] = useState<ISelectItem[]>([]);
  const [startingCount, setStartingCount] = useState(0);
  const [isStartingCountLoading, setIsStartingCountLoading] = useState(true);
  const [assignmentId, setAssignmentId] = useState<number | null>(null);
  const [assignmentName, setAssignmentName] = useState<string>('');
  const [isAssignNameValid, setIsAssignNameValid] = useState<boolean>(true);
  const [isAssignNameMaxLengthValid, setIsAssignNameMaxLengthValid] =
    useState<boolean>(true);
  const [touchedFields, setTouchedFields] = useState<{
    assignmentName: boolean;
  }>({
    assignmentName: false,
  });
  const [isAssignmentExist, setIsAssignmentExist] = useState<boolean>(false);

  const shouldFetchCount = masterDocListId > 0;
  const {data} = useGetBulkTagSelectedDocCount(
    {masterDocListId},
    shouldFetchCount
  );

  const handleSortChange = (value: string) => {
    setFormValues((values) => ({
      ...values,
      sortOrder: value,
    }));
  };
  // ------------------

  const quickAssingParams = {
    elementId: 'divBulkAction',
    actionType: 'QUICKASSIGNMENT',
    sourceType: 'DOCEXPLORER',
  };

  const {data: reveiwersList} = useGetUnAssignedUsers();
  const {data: codingForm} = useGetQuickAssign(quickAssingParams);

  useEffect(() => {
    if (data?.docCount !== undefined && data.docCount !== -1) {
      setStartingCount(data.docCount);
      setIsStartingCountLoading(false);
    }
  }, [data?.docCount]);

  useEffect(() => {
    const reveiwers: IOptions[] = [];
    reveiwersList?.notAssignedUsers.map((item) => {
      reveiwers.push({
        label: item.reviewerName,
        value: false,
      });
    });
    setReveiwersOptions(reveiwers);
  }, [reveiwersList]);

  useEffect(() => {
    const codingFormDataList: ISelectItem[] = [];
    codingForm?.codingFormsList.map((item) => {
      codingFormDataList.push({
        id: item.codingFormID,
        name: item.codingFormName,
      });
    });
    setCodingFormOptions(codingFormDataList);
  }, [codingForm]);

  const handleAssignNameChange = (value: string) => {
    setFormValues((values) => ({...values, assignName: value}));
    setAssignmentName(value);
    setIsAssignmentExist(false);
    setIsAssignNameValid(true);
    if (value.trim() !== '') {
      setIsAssignNameMaxLengthValid(value.length <= MAX_FOLDER_NAME_LENGTH);
    } else {
      setIsAssignNameMaxLengthValid(true);
    }
  };
  // ------------------

  const handleBlurAssignName = (fieldName: keyof typeof touchedFields) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };
  const setErrorMsg = () => {
    if (touchedFields.assignmentName && !assignmentName) {
      return t(`${enQuickBatch}.errors.nameRequired`);
    }
    if (!isAssignNameValid && assignmentName) {
      return t(`${enQuickBatch}.errors.noSpecialChar`);
    }
    if (!isAssignNameMaxLengthValid) {
      return t(`${enQuickBatch}.errors.maxLengthMsg`, {
        max: MAX_FOLDER_NAME_LENGTH,
      });
    }
    if (isAssignmentExist) {
      return t(`${enQuickBatch}.errors.duplicateAssignment`);
    }
    return '';
  };

  const isAssignNameInputValid =
    !isAssignNameValid ||
    !isAssignNameMaxLengthValid ||
    (touchedFields.assignmentName && !assignmentName) ||
    isAssignmentExist;

  const handleCodingFormChange = (value: ISelectItem | null) => {
    setFormValues((values) => ({
      ...values,
      codingForm: value ? String(value.id) : null,
    }));
  };
  // ------------------

  const handleReviewersChange = (reviewerValues: IOptions[]) => {
    setFormValues((values) => ({...values, reviewers: reviewerValues}));
  };
  // ------------------

  useEffect(() => {
    if (
      Object.values(formValues).every(
        (val, index) => index === 2 || !isEmpty(val)
      ) &&
      startingCount > 0
    ) {
      setDisableContinue(false);
    } else {
      setDisableContinue(true);
    }
  }, [formValues, startingCount]);

  const {mutate: quickCreate, isPending: isQuickCreateLoading} = useQuickCreate(
    (response) => {
      setAssignmentId(response.assignmentid);
      const selectedReviewers = formValues.reviewers.filter((r) => r.value);

      if (
        response.assignmentid < 0 &&
        response.updateStatus.toLocaleLowerCase() === 'duplicateassignment'
      ) {
        setIsAssignmentExist(true);
      } else {
        if (selectedReviewers.length > 0) {
          const reviewersData = selectedReviewers
            .filter((r) => r.value)
            .map((r) => {
              const originalReviewer = reveiwersList?.notAssignedUsers.find(
                (item) => item.reviewerName === r.label
              );

              return {
                reviewerId: originalReviewer?.reviewerId || 0,
                emailId: originalReviewer?.emailId || '',
              };
            })
            .filter((r) => r.reviewerId > 0 && r.emailId);

          const addReviewersPayload = {
            assignmentId: response.assignmentid,
            reviewers: reviewersData as any,
          };

          addAssignedReviewers(addReviewersPayload);
        } else {
          onClose();
          if (onContinue) {
            onContinue(response.assignmentid, startingCount, 'quickBatch');
          }
        }
      }
    },
    (_error) => {
      if (axios.isAxiosError(_error)) {
        const res = _error?.response?.data;
        if (
          res?.status === 400 &&
          res?.detail.toLocaleLowerCase().includes('special characters')
        ) {
          setIsAssignNameValid(false);
        }
      }
    }
  );

  const {mutate: addAssignedReviewers, isPending: isAddReviewersLoading} =
    useAddAssignedReviewers(() => {
      onClose();
      if (onContinue) {
        onContinue(assignmentId ?? 0, startingCount, 'quickBatch');
      }
    });
  // ------------------

  const handleQuickCreate = () => {
    const selectedCodingForm = codingFormOptions.find(
      (form) => String(form.id) === formValues.codingForm
    );

    const codingFormId = selectedCodingForm?.id ?? formValues.codingForm;
    const codingFormName = selectedCodingForm?.name ?? '';
    const quickCreatePayload = {
      selectedSeq: '2,3,4,1',
      assignmentID: 0,
      ParentAssignmentGroupID: -1,
      docPresentationSorting: {
        isCategory: formValues.sortOrder === 'optimizedSort',
        isMetadata: formValues.sortOrder !== 'optimizedSort',
        isImportList: false,
        selectedMetaDataFieldId: -82,
        metaDataDirection: true,
      },
      assignmentName: formValues.assignName,
      codingFormID: codingFormId,
      selectCodingFormLists: JSON.stringify([
        {
          CodingFormId: codingFormId,
          CodingFormName: codingFormName,
          IsDefault: true,
          Sortorder: 1,
        },
      ]),
      additionalPreferences: {
        isShowDefaultViewTab: true,
        isShowAnalyticsPanel: true,
        isEnableRedactions: true,
      },
      sortorder: formValues.sortOrder,
    };
    quickCreate(quickCreatePayload);
  };
  const IsContinueDisabled =
    disableContinue || isQuickCreateLoading || isAddReviewersLoading;
  return (
    <ArrDrawer
      isOpen={isOpen}
      onClose={onClose}
      title=""
      side="right"
      size="small"
      showButton={false}
      showHeader={false}
      className="quick-batch-flyout"
      ariaLabel="Quick Batch Flyout"
    >
      <Box className="quick-batch-flyout__header">
        <Box className="title-box" tabIndex={0}>
          <ArrTooltipTitle
            classNames={{
              title: 'typo-text headline3_size title',
            }}
            title={t(`${enQuickBatch}.tooltip.flyoutTitle`)}
            tooltipTitle={t(`${enQuickBatch}.tooltip.flyoutTitle`)}
            tooltipContent={t(`${enQuickBatch}.tooltip.flyoutContent`)}
            data-testid="flyout-title"
            dataTestId="flyout-qb"
          />
        </Box>
        <Box className="action-buttons-box">
          <ArrButton
            variant="secondary"
            size="medium"
            onClick={() => {
              onClose();
            }}
            disabled={false}
            aria-label={t('common.cancel')}
            dataTestId="quick-batch-cancel-button"
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            className={IsContinueDisabled ? 'btn-disabled' : ''}
            variant="primary"
            size="medium"
            type="submit"
            aria-label={t('common.continue')}
            dataTestId="quick-batch-continue-button"
            onClick={handleQuickCreate}
            isAriaDisabled={IsContinueDisabled}
            tabIndex={0}
          >
            {t('common.continue')}
          </ArrButton>
        </Box>
      </Box>
      {/* Flyout Content */}
      <Box className="quick-batch-flyout__content">
        <QuickBatch
          totalSelected={selectedIds.length}
          codingFormOptions={codingFormOptions || []}
          sortOptions={sortOptions}
          reviewerOptions={reveiwersOptions || []}
          onAssignNameChange={handleAssignNameChange}
          onCodingFormChange={handleCodingFormChange}
          onSortChange={handleSortChange}
          onReviewerChange={handleReviewersChange}
          startingCount={startingCount}
          isStartingCountLoading={isStartingCountLoading}
          isAssignmentValid={isAssignNameInputValid}
          assignmentName={assignmentName}
          handleBlurAssignName={handleBlurAssignName}
          setErrorMsg={setErrorMsg}
        />
      </Box>
    </ArrDrawer>
  );
};

export {QuickBatchFlyout};
export default QuickBatchFlyout;
